// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Card, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Label, Translation } from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { RefundFields } from '../refund-fields/refund-fields';
import { RefundFieldsDisplay } from '../refund-fields/refund-fields-display';
import { useRefundOptions } from './use-refund-options';

export const RefundOptions = defineComponent({
  displayName: 'RefundOptions',
  useSetup: useRefundOptions,
  View: ({
    refundType,
    isFull,
    currency,
    totalShares,
    totalAmount,
    defaultPartialPercentage,
    defaultPartialShares,
    defaultPartialAmount,
    onRefundTypeChange,
    onBlur,
  }) => (
    <fieldset className="space-y-3">
      <Label component="legend" className="text-base font-semibold">
        <Translation
          ns="components"
          i18nKey="ticker-modal.refund-percentage-shares-amount"
        />
      </Label>

      <RadioGroup
        value={refundType}
        onChange={e => onRefundTypeChange(e.target.value as 'full' | 'partial')}
        className="space-y-2"
      >
        <Card
          variant="outlined"
          className="!rounded-lg !bg-white !p-3 transition-colors hover:!bg-gray-50"
        >
          <div className="flex items-start gap-4">
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={
                <Translation ns="components" i18nKey="ticker-modal.total-refund" />
              }
              className="m-0"
            />
            <RefundFieldsDisplay
              percentage={100}
              shares={totalShares}
              amount={totalAmount}
              currency={currency}
              data-testid="refund-fields-full"
            />
          </div>
        </Card>

        <Card
          variant="outlined"
          className="!rounded-lg !bg-white !p-3 transition-colors hover:!bg-gray-50"
        >
          <div className="flex items-start gap-4">
            <FormControlLabel
              value="partial"
              control={<Radio />}
              label={
                <Translation
                  ns="components"
                  i18nKey="ticker-modal.partial-refund"
                />
              }
              className="m-0"
            />
            {isFull ? (
              <RefundFieldsDisplay
                percentage={defaultPartialPercentage}
                shares={defaultPartialShares}
                amount={defaultPartialAmount}
                currency={currency}
                data-testid="refund-fields-partial"
              />
            ) : (
              <RefundFields
                currency={currency}
                onBlur={onBlur}
                data-testid="refund-fields-partial"
              />
            )}
          </div>
        </Card>
      </RadioGroup>
    </fieldset>
  ),
});
